/**
 * Los puertos de conexión: los cuatro puntitos que aparecen sobre un nodo
 * cuando la herramienta conector está activa, y el resaltado del nodo que
 * recibiría la flecha.
 *
 * Sólo se muestran con el conector activo: en `select` el hover queda limpio
 * y no tapan el contenido de las tarjetas.
 *
 * Va en la capa de PANTALLA (sobre `app.stage`, como la marquesina) y no en el
 * mundo: los puertos tienen que medir lo mismo en cualquier zoom, igual que los
 * handles de resize de una tarjeta.
 */

import { Graphics } from "pixi.js";
import { type EdgeSide } from "../model/types.ts";
import { UI_SELECTION, UI_PAPER } from "./uiColors.ts";

/** Radio visible del puerto, en px de pantalla. */
const PORT_R = 4;
/** Radio del puerto bajo el puntero. */
const PORT_R_ACTIVE = 6;
/**
 * Radio de TOQUE, bastante mayor que el visible: un punto de 4 px es imposible
 * de cazar con precisión, sobre todo en trackpad. Mismo criterio que las bandas
 * ampliadas del borde de un contenedor (`GROUP_EDGE_PX` en CanvasView).
 */
const PORT_HIT_R = 12;

/** Un rectángulo en píxeles de pantalla. */
export interface ScreenRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const SIDES: EdgeSide[] = ["top", "right", "bottom", "left"];

/** Centro del puerto de un lado, en px de pantalla. */
export function portCenter(rect: ScreenRect, side: EdgeSide): { x: number; y: number } {
  const cx = rect.x + rect.width / 2;
  const cy = rect.y + rect.height / 2;
  switch (side) {
    case "top":
      return { x: cx, y: rect.y };
    case "bottom":
      return { x: cx, y: rect.y + rect.height };
    case "left":
      return { x: rect.x, y: cy };
    case "right":
      return { x: rect.x + rect.width, y: cy };
  }
}

/** ¿Cae el punto de pantalla sobre una manija de extremo? Devuelve su índice. */
export function endpointAt(
  points: { x: number; y: number }[],
  sx: number,
  sy: number,
): number | null {
  for (let i = 0; i < points.length; i++) {
    if (Math.hypot(sx - points[i].x, sy - points[i].y) <= PORT_HIT_R) return i;
  }
  return null;
}

/** El puerto bajo el punto de pantalla, si hay alguno dentro del radio de toque. */
export function portAt(rect: ScreenRect, sx: number, sy: number): EdgeSide | null {
  let best: EdgeSide | null = null;
  let bestDist = PORT_HIT_R;
  for (const side of SIDES) {
    const p = portCenter(rect, side);
    const d = Math.hypot(sx - p.x, sy - p.y);
    if (d <= bestDist) {
      bestDist = d;
      best = side;
    }
  }
  return best;
}

export interface PortsState {
  /** Nodo bajo el puntero cuyos puertos se muestran, o null. */
  node: ScreenRect | null;
  /** Lado cuyo puerto está bajo el puntero: se agranda y se pinta lleno. */
  active: EdgeSide | null;
  /** Nodo que se llevaría la conexión si se soltara ahora. */
  target: ScreenRect | null;
  /** Puerto del destino bajo el puntero durante un arrastre: se resalta. */
  targetActive?: EdgeSide | null;
  /**
   * Extremos de la arista seleccionada, en píxeles de pantalla. Se dibujan como
   * manijas: arrastrarlas desengancha ese extremo y lo lleva a otro nodo.
   */
  endpoints?: { x: number; y: number }[] | null;
}

export class EdgePorts {
  readonly graphics = new Graphics();

  /** Lo último dibujado, para no repintar en cada frame sin motivo. */
  private lastKey = "";

  constructor() {
    this.graphics.eventMode = "none";
  }

  update(state: PortsState): void {
    const key = state.node
      ? `${state.node.x.toFixed(1)},${state.node.y.toFixed(1)},${state.node.width.toFixed(1)},${state.node.height.toFixed(1)},${state.active ?? "-"}`
      : "-";
    const targetKey = state.target
      ? `${state.target.x.toFixed(1)},${state.target.y.toFixed(1)},${state.target.width.toFixed(1)},${state.target.height.toFixed(1)},${state.targetActive ?? "-"}`
      : "-";
    const endsKey = (state.endpoints ?? [])
      .map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`)
      .join(";");
    const full = `${key}|${targetKey}|${endsKey}`;
    if (full === this.lastKey) return;
    this.lastKey = full;

    const g = this.graphics;
    g.clear();

    // El nodo que recibiría la flecha, en el mismo azul que ya usa el
    // drop-target de un contenedor: "soltá acá y esto queda conectado".
    if (state.target) {
      g.roundRect(state.target.x, state.target.y, state.target.width, state.target.height, 4);
      g.stroke({ width: 2, color: UI_SELECTION, alpha: 1 });
      // Sus cuatro puertos, para apuntar al punto exacto de llegada.
      for (const side of SIDES) {
        const p = portCenter(state.target, side);
        const isActive = side === state.targetActive;
        g.circle(p.x, p.y, isActive ? PORT_R_ACTIVE : PORT_R);
        g.fill(isActive ? UI_SELECTION : UI_PAPER);
        g.stroke({ width: 1.5, color: UI_SELECTION, alpha: 1 });
      }
    }

    // Manijas de los extremos de la arista seleccionada.
    for (const p of state.endpoints ?? []) {
      g.circle(p.x, p.y, PORT_R_ACTIVE);
      g.fill(UI_PAPER);
      g.stroke({ width: 2, color: UI_SELECTION, alpha: 1 });
    }

    if (!state.node) return;
    for (const side of SIDES) {
      const p = portCenter(state.node, side);
      const isActive = side === state.active;
      g.circle(p.x, p.y, isActive ? PORT_R_ACTIVE : PORT_R);
      g.fill(isActive ? UI_SELECTION : UI_PAPER);
      g.stroke({ width: 1.5, color: UI_SELECTION, alpha: 1 });
    }
  }

  hide(): void {
    if (this.lastKey === "") return;
    this.lastKey = "";
    this.graphics.clear();
  }

  destroy(): void {
    this.graphics.destroy();
  }
}
